import React, { useState } from 'react';
import { Search, Menu, X } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import catLogo from '../assets/ic_cat.svg';
import playLogo from '../assets/ic_play.svg';

interface HeaderProps {
  onSearch?: (query: string) => void;
}

export default function Header({ onSearch }: HeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedQuery = searchQuery.trim();
    if (trimmedQuery) {
      onSearch?.(trimmedQuery);
      navigate(`/search?q=${encodeURIComponent(trimmedQuery)}`);
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 bg-[#13111d] text-white z-50">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center group">
            <div className="flex items-center space-x-3">
              <img 
                src={catLogo} 
                alt="OXO" 
                className="h-8 w-auto transition-transform duration-300 ease-in-out group-hover:rotate-12" 
              />
              <img 
                src={playLogo} 
                alt="Play" 
                className="h-8 w-auto transition-transform duration-300 ease-in-out group-hover:scale-110" 
              />
            </div>
          </Link>

          <div className="hidden md:flex flex-1 items-center justify-center">
            <form onSubmit={handleSearch} className="w-96">
              <div className="relative">
                <input
                  type="text"
                  placeholder={t('common.search')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-[#1E1A2E] text-white px-4 py-2 pl-10 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-400"
                />
                <button type="submit" className="absolute left-3 top-2.5">
                  <Search className="w-5 h-5 text-gray-400" />
                </button>
              </div>
            </form>
          </div>

          <nav className="hidden md:flex items-center space-x-8">
            <Link to="/about" className="hover:text-purple-400">
              {t('common.about_us')}
            </Link>
            <Link to="/contact" className="hover:text-purple-400">
              {t('common.contact_us')}
            </Link>
          </nav>

          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 hover:bg-[#1E1A2E] rounded-lg"
          >
            {isMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>

      {isMenuOpen && (
        <div className="md:hidden bg-[#1a1625] text-white">
          <nav className="px-4 pt-2 pb-4">
            <form onSubmit={handleSearch} className="relative mt-4">
              <input
                type="text"
                placeholder={t('common.search')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-[#13111d] text-white px-4 py-2 rounded-full w-full focus:outline-none focus:ring-2 focus:ring-purple-400"
              />
              <button type="submit" className="absolute right-3 top-2.5">
                <Search className="w-5 h-5 text-gray-400" />
              </button>
            </form>
            <ul className="space-y-2 mt-4">
              <li>
                <Link to="/about" className="block py-2 hover:text-purple-400">
                  {t('common.about_us')}
                </Link>
              </li>
              <li>
                <Link to="/contact" className="block py-2 hover:text-purple-400">
                  {t('common.contact_us')}
                </Link>
              </li>
            </ul>
          </nav>
        </div>
      )}
    </header>
  );
}